import React, { useEffect, useState } from 'react';
import styles from './OnlineTransactions.module.css';
import {
  getBalanceHistory,
  BalanceHistoryItem,
} from '../../services/balanceHistory.service';
import { TableLoading } from '../Loading';
import { formatNumberWithCommas } from '../../utils/helpers/formatting.helpers';
import { formatDate } from '../../utils/helpers/date.helpers';
import { Pagination, PaginationItem, Stack } from '@mui/material';
import { ReactComponent as SortIcon } from '../../assets/icons/sort.svg';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import {
  getChildBalanceReport,
  ChildBalanceReport,
  EMPTY_CHILD_BALANCE_REPORT,
} from '../../services/getChildBalanceReport.service';
import { authService } from '../../services/auth.service';

type Sorting = 'desc' | 'asc' | 'def';

const PAGE_SIZE = 10;

function getLastWeekDate() {
  const today = new Date();
  const lastWeek = new Date(today);
  lastWeek.setDate(today.getDate() - 7);
  const year = lastWeek.getFullYear();
  const month = String(lastWeek.getMonth() + 1).padStart(2, '0');
  const day = String(lastWeek.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateForInput(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const OnlineTransactionsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<BalanceHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFrom, setDateFrom] = useState(getLastWeekDate());
  const [dateTo, setDateTo] = useState(formatDateForInput(new Date()));
  const [timeFrom, setTimeFrom] = useState('00:00');
  const [timeTo, setTimeTo] = useState('23:59');
  const [searchTrigger, setSearchTrigger] = useState(0);
  const [sorting, setSorting] = useState<Sorting>('def');
  const [sortedTransactions, setSortedTransactions] = useState<BalanceHistoryItem[]>([]);
  const [siblingCount, setSiblingCount] = useState(2);
  const { environments, selectedEnvironmentId, setSelectedEnvironmentId, isAdmin } = useEnvironment();
  const currentUser = authService.getStoredUser();
  const [report, setReport] = useState<ChildBalanceReport>(EMPTY_CHILD_BALANCE_REPORT);

  const getSignedAmount = (item: BalanceHistoryItem) =>
    item.type === 'cashOut' ? -item.amount : item.amount;

  useEffect(() => {
    const sorted =
      sorting === 'def'
        ? transactions
        : sorting === 'desc'
        ? [...transactions].sort((a, b) => getSignedAmount(b) - getSignedAmount(a))
        : [...transactions].sort((a, b) => getSignedAmount(a) - getSignedAmount(b));
    setSortedTransactions(sorted);
  }, [sorting, transactions]);

  useEffect(() => {
    setLoading(true);
    let startDate = `${dateFrom}`;
    let endDate = `${dateTo}`;
    if (timeFrom) startDate = `${dateFrom}T${timeFrom}`;
    if (timeTo) endDate = `${dateTo}T${timeTo}`;

    getBalanceHistory(
      {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        includeTypes: ['CRIPTO_DEPOSIT', 'CRIPTO_WITHDRAW'],
        environmentID: selectedEnvironmentId ? Number(selectedEnvironmentId) : undefined,
      },
      true,
    )
      .then((data) => {
        setTransactions(data.history);
        setTotal(data.total || 0);
      })
      .catch((err) => console.error('Failed to fetch online transactions:', err))
      .finally(() => setLoading(false));
  }, [page, searchTrigger, selectedEnvironmentId]);

  // Total transactions report (cash / online / total) for the searched range.
  // Admin: scope to the selected environment (omit when "All Environments").
  // Non-admin: scope to the logged-in user's own environment.
  useEffect(() => {
    if (!currentUser?.id) return;
    let startDate = `${dateFrom}`;
    let endDate = `${dateTo}`;
    if (timeFrom) startDate = `${dateFrom}T${timeFrom}`;
    if (timeTo) endDate = `${dateTo}T${timeTo}`;

    const environmentID = isAdmin
      ? selectedEnvironmentId
        ? Number(selectedEnvironmentId)
        : null
      : currentUser?.environment?.id ?? null;

    getChildBalanceReport({
      userId: currentUser.id,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      environmentID,
    })
      .then(setReport)
      .catch(() => setReport(EMPTY_CHILD_BALANCE_REPORT));
  }, [searchTrigger, selectedEnvironmentId]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 890) setSiblingCount(0);
      else if (window.innerWidth < 1080) setSiblingCount(1);
      else setSiblingCount(2);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = () => {
    setPage(1);
    setSearchTrigger((v) => v + 1);
  };

  return (
    <main className={styles.main}>
      <h1 className={styles.title}>Online Transactions</h1>
      {isAdmin && (
        <div className={styles.environmentSelect}>
          <label className={styles.label}>Environment</label>
          <select
            className={styles.select}
            value={selectedEnvironmentId}
            onChange={(e) => setSelectedEnvironmentId(e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">All Environments</option>
            {environments.map((env) => (
              <option key={env.id} value={env.id}>
                {env.name} - {env.host}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className={styles.searchByDate}>
        <label className={styles.label}>Search by Date and Time</label>
        <div className={styles.dateInputGroup}>
          <div>
            <span className={styles.dateIcon}>📅</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <input
              type="time"
              className={styles.timeInput}
              value={timeFrom}
              onChange={(e) => setTimeFrom(e.target.value)}
            />
          </div>
          <div>
            <span className={styles.toText}>To</span>
            <input
              type="date"
              className={styles.dateInput}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <input
              type="time"
              className={styles.timeInput}
              value={timeTo}
              onChange={(e) => setTimeTo(e.target.value)}
            />
          </div>
        </div>
        <button className={styles.searchBtn} onClick={handleSearch}>
          Search
        </button>
      </div>
      <TableLoading
        loading={loading}
        rows={8}
        columns={7}
        text="Loading online transactions..."
        className={styles.tableWrapper}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>User</th>
                <th>
                  <div
                    className={styles.sortable}
                    onClick={() =>
                      setSorting((prev) => {
                        if (prev === 'asc') return 'desc';
                        if (prev === 'desc') return 'asc';
                        return 'asc';
                      })
                    }
                  >
                    Amount <div className={styles.sortIcon}><SortIcon /></div>
                  </div>
                </th>
                <th>Type</th>
                <th>IP Address</th>
                <th>Comments</th>
                <th>Added By</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {sortedTransactions.map((tx, i) => (
                <tr key={i}>
                  <td>{tx.user}</td>
                  <td
                    className={
                      tx.type === 'cashIn'
                        ? styles.inCol
                        : tx.type === 'cashOut'
                        ? styles.outCol
                        : ''
                    }
                  >
                    {tx.type === 'cashIn' ? '+' : tx.type === 'cashOut' ? '-' : ''}
                    {formatNumberWithCommas(tx.amount)}
                  </td>
                  <td>{tx.type}</td>
                  <td>{tx.ipAddress || '-'}</td>
                  <td>{tx.comments || '-'}</td>
                  <td>{tx.addedBy || '-'}</td>
                  <td>{formatDate(tx.createdAt)}</td>
                </tr>
              ))}
              {transactions.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center' }}>
                    No online transactions found.
                  </td>
                </tr>
              )}
              <tr className={styles.totalRow}>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td style={{ border: '0', background: '#fafafa' }}></td>
                <td className={styles.totalLabel} data-label="TOTAL">TOTAL</td>
                <td className={styles.inCol} data-label="In">
                  {formatNumberWithCommas(report.cryptoCashIn)}
                </td>
                <td className={styles.outCol} data-label="Out">
                  {formatNumberWithCommas(report.cryptoCashOut)}
                </td>
                <td className={styles.profitCol} data-label="Total">
                  {formatNumberWithCommas(report.cryptoProfit)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </TableLoading>
      {/* Pagination controls — below the transaction list */}
      <div className={styles.paginationControls}>
          {total && Math.ceil(total / PAGE_SIZE) > 0 ? (
            <Stack spacing={1}>
              <Pagination
                onChange={(_e, value) => setPage(value)}
                renderItem={(item) => (
                  <PaginationItem
                    {...item}
                    slots={{
                      previous: () => (
                        <button
                          onClick={() => { if (page > 1) setPage((p) => p - 1); }}
                          disabled={page === 1}
                          className={styles.searchBtn + ' ' + styles.paginationBtn}
                        >
                          Prev
                        </button>
                      ),
                      next: () => (
                        <button
                          onClick={() => {
                            if (page < Math.ceil(total / PAGE_SIZE)) setPage((p) => p + 1);
                          }}
                          disabled={page >= Math.ceil(total / PAGE_SIZE)}
                          className={styles.searchBtn + ' ' + styles.paginationBtn}
                        >
                          Next
                        </button>
                      ),
                    }}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: '#313485',
                        color: '#fff',
                        padding: '5px',
                        '&:hover': { backgroundColor: '#232665' },
                      },
                      padding: '5px',
                    }}
                  />
                )}
                count={Math.ceil(total / PAGE_SIZE)}
                page={page}
                color="primary"
                size="medium"
                shape="rounded"
                siblingCount={siblingCount}
                boundaryCount={1}
              />
            </Stack>
          ) : (
            <button
              disabled={page === 1}
              className={styles.searchBtn + ' ' + styles.paginationBtn}
            >
              1
            </button>
          )}
        </div>
    </main>
  );
};

export default OnlineTransactionsPage;
